<script setup lang="ts">
import Button from 'primevue/button'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { TGymAccessCheckIn } from '../../api/queries'
import { useGymDateDisplay } from '../../composables/useGymDateDisplay'

type TToastMessage = {
  summary?: string
  accessResult?: TGymAccessCheckIn
}

const props = defineProps<{
  message: TToastMessage
  closeCallback: () => void
}>()

const { t } = useI18n()
const { formatCalendarDate } = useGymDateDisplay()

const result = computed(() => props.message.accessResult)

const toneClass = computed(() => {
  const tone = result.value?.tone
  if (tone === 'success') {
    return 'border-green-500 bg-green-50 dark:bg-green-950/40'
  }
  if (tone === 'info') {
    return 'border-sky-500 bg-sky-50 dark:bg-sky-950/40'
  }
  return 'border-red-500 bg-red-50 dark:bg-red-950/40'
})

const iconClass = computed(() => {
  const tone = result.value?.tone
  if (tone === 'success') {
    return 'pi pi-check-circle text-green-600 dark:text-green-400'
  }
  if (tone === 'info') {
    return 'pi pi-info-circle text-sky-600 dark:text-sky-400'
  }
  return 'pi pi-times-circle text-red-600 dark:text-red-400'
})

const headline = computed(
  () =>
    props.message.summary?.trim() ||
    (result.value?.allowed ? t('gym.ACCESS_ALLOWED') : t('gym.ACCESS_DENIED')),
)
</script>

<template>
  <div
    v-if="result"
    class="flex w-full max-w-md gap-3 rounded-xl border-l-4 p-4 shadow-md"
    :class="toneClass"
  >
    <i :class="iconClass" class="mt-0.5 shrink-0 text-xl" aria-hidden="true" />

    <div class="min-w-0 flex-1">
      <div class="flex items-start justify-between gap-2">
        <p class="text-sm font-semibold uppercase tracking-wide">
          {{ headline }}
        </p>
        <Button
          type="button"
          icon="pi pi-times"
          severity="secondary"
          text
          rounded
          size="small"
          class="!h-7 !w-7 shrink-0"
          :aria-label="t('gym.CLOSE_ACCESS')"
          @click="closeCallback"
        />
      </div>

      <p class="mt-1 text-base font-bold leading-snug break-words">
        {{ result.member?.full_name ?? t('gym.ACCESS_UNKNOWN_MEMBER') }}
      </p>

      <p
        v-if="result.member"
        class="text-sm text-surface-600 dark:text-surface-300"
      >
        #{{ result.member.member_code }}
      </p>

      <p
        v-if="result.subscription?.plan_name"
        class="mt-1 text-sm font-medium text-surface-700 dark:text-surface-200"
      >
        {{ result.subscription.plan_name }}
      </p>

      <p
        class="mt-2 text-sm leading-snug break-words text-surface-700 dark:text-surface-200"
      >
        {{ result.message }}
      </p>

      <dl
        v-if="result.subscription"
        class="mt-2 grid grid-cols-1 gap-1 text-xs text-surface-600 dark:text-surface-400 sm:grid-cols-2"
      >
        <div v-if="result.subscription.paid_through">
          <dt class="uppercase tracking-wide">{{ t('gym.PAID_THROUGH') }}</dt>
          <dd class="font-semibold text-surface-800 dark:text-surface-200">
            {{ formatCalendarDate(result.subscription.paid_through) }}
          </dd>
        </div>
        <div v-if="result.subscription.current_period_end">
          <dt class="uppercase tracking-wide">
            {{ t('gym.NEXT_PAYMENT_DATE') }}
          </dt>
          <dd class="font-semibold text-surface-800 dark:text-surface-200">
            {{ formatCalendarDate(result.subscription.current_period_end) }}
          </dd>
        </div>
        <div v-if="result.subscription.grace_until">
          <dt class="uppercase tracking-wide">{{ t('gym.GRACE_UNTIL') }}</dt>
          <dd class="font-semibold text-surface-800 dark:text-surface-200">
            {{ formatCalendarDate(result.subscription.grace_until) }}
          </dd>
        </div>
      </dl>
    </div>
  </div>
</template>
